import React, { useEffect, useState } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { deleteAdvertisement, searchAdvertisement } from '../../constant/Constant';
import { Edit, Trash } from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';
import AppPagination from '../../components/Pagination/AppPagination';
import { setPagination } from '../../redux/Actions/TableAction';
import EditButton from '../../components/Edit/EditButton';
import DeleteButton from '../../components/Delete/DeleteButton';
import { getSelectedUnit } from '../../storage/Storage';
import { hasPermission } from '../../utils/permissionUtils';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';

function Advertisement() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  // Search state variables
  const [optionSelect, setOptionSelect] = useState('');
  const [valueSearched, setValueSearched] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Calculate base index for serial numbers
  const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

  // Permission checks using hasPermission utility
  const canEditAds = hasPermission('advertisement', 'write');
  const canReadAds = hasPermission('advertisement', 'read');
  const canDeleteAds = hasPermission('advertisement', 'delete');
  const canAddAds = hasPermission('advertisement', 'write');

  console.log({canEditAds, canReadAds, canDeleteAds, canAddAds});

  useEffect(() => {
    // Only fetch advertisements if user has read permission
    if (canReadAds) {
      getAdvertisements();
    }
  }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadAds]);

  const getAdvertisements = (searchKey, searchValue) => {
    if (!canReadAds) {
      toast.error('You do not have permission to view advertisements');
      return;
    }

    setIsSearching(true);

    const searchObj = {
      unitIds: getSelectedUnit()?._id
    };

    // Only add search key/value if both are provided
    if (searchKey && searchValue) {
      searchObj[searchKey] = searchValue;
    }

    const json = {
      page: TableDataReducer.pagination.page,
      limit: TableDataReducer.pagination.limit,
      search: searchObj
    };

    HitApi(json, searchAdvertisement).then((res) => {
      console.log("res", res);
      if (res?.message === "Advertisement collection retrieved successfully") {
        const adData = Array.isArray(res?.data) ? res?.data : [];
        dispatch(setApiJson(adData));

        // Update pagination data
        const paginationData = {
          total: res?.totalDocs || res?.totalCount || adData.length || 0,
          page: TableDataReducer.pagination.page,
          limit: TableDataReducer.pagination.limit
        };

        dispatch(setPagination(paginationData));
      } else {
        dispatch(setApiJson([]));
        toast.error('Failed to fetch advertisements. Please try again.');
      }
    }).catch(error => {
      console.error("Error fetching advertisements:", error);
      dispatch(setApiJson([]));
      toast.error('Error fetching advertisements. Please try again.');
    }).finally(() => {
      setIsSearching(false);
    });
  };

  // Search functionality
  const handleSearch = () => {
    if (!canReadAds) {
      toast.error('You do not have permission to search advertisements');
      return;
    }

    if (!optionSelect) {
      toast.error('Please select a search option');
      return;
    }
    if (!valueSearched || valueSearched.trim() === '') {
      toast.error('Please enter a search value');
      return;
    }
    getAdvertisements(optionSelect, valueSearched.trim());
  };

  // Clear search functionality
  const handleClearSearch = () => {
    if (!canReadAds) {
      toast.error('You do not have permission to access advertisements');
      return;
    }

    setOptionSelect('');
    setValueSearched('');
    getAdvertisements();
  };

  // Search options based on table keys
  const options = [
    { label: 'Description', value: 'description' },
    { label: 'Discount', value: 'discount' },
    { label: 'Status', value: 'isActive' },
  ];

  const handleEdit = (row) => {
    if (!canEditAds) {
      toast.error('You do not have permission to edit advertisements');
      return;
    }
    navigate(`/advertiesment/editadvertiesment/${row._id}`);
  };

  const handleDeleteAdvertiesment = (row) => {
    if (!canDeleteAds) {
      toast.error('You do not have permission to delete advertisements');
      return;
    }

    if (window.confirm(`Are you sure you want to delete this advertisement?`)) {
      const json = {
        _id: row._id
      };

      console.log("json", json);

      HitApi(json, deleteAdvertisement).then((res) => {
        console.log('res', res);
        if (res?.success) {
          toast.success('Advertisement deleted successfully!');
          getAdvertisements();
        } else {
          toast.error('Failed to delete advertisement. Please try again.');
        }
      }).catch(error => {
        console.error("Error deleting advertisement:", error);
        toast.error('Error occurred while deleting advertisement. Please try again.');
      });
    }
  };

  const renderStatus = (status) => {
    if (status === undefined || status === null) return null;

    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";

    switch (status.toLowerCase()) {
      case "active":
        bgColor = "bg-green-100";
        textColor = "text-green-800";
        break;
      case "inactive":
        bgColor = "bg-red-100";
        textColor = "text-red-800";
        break;
      case "pending":
        bgColor = "bg-yellow-100";
        textColor = "text-yellow-800";
        break;
      default:
        bgColor = "bg-gray-100";
        textColor = "text-gray-800";
        break;
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Define base table headers
  const baseTableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      render: (_, row, index) => {
        // Calculate serial number based on current page and limit
        const serialNumber = baseIndex + index + 1;
        return (
          <div className="text-center">
            <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
          </div>
        );
      }
    },
    {
      title: "Image",
      key: "img",
      render: (value, row) => (
        value ?
          <img src={value} alt="Advertisement" className="w-16 h-16 object-cover rounded" /> :
          <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
            <span className="text-xs text-gray-500">No Image</span>
          </div>
      )
    },
    {
      title: "Description",
      key: "description",
      render: (value) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600">
            {value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : 'No description'}
          </span>
        </div>
      )
    },
    {
      title: "Discount",
      key: "discount",
      render: (value) => (
        <span className="px-2 py-1 bg-blue-100 rounded text-sm font-medium text-blue-800">
          {value ? `${value}%` : 'N/A'}
        </span>
      )
    },
    {
      title: "Status",
      key: "isActive",
      render: (value) => {
        // Use the actual boolean value directly
        const status = value === true ? "active" : "inactive";
        return renderStatus(status);
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (value, row) => {
        // Check if user has any action permissions
        const hasAnyPermission = canEditAds || canDeleteAds;

        if (!hasAnyPermission) {
          return (
            <div className="flex justify-center">
              <span className="text-sm text-gray-400">No actions available</span>
            </div>
          );
        }

        return (
          <div className="flex justify-center space-x-2">
            {canEditAds && (
              <EditButton
                onEdit={() => handleEdit(row)}
                itemName="advertisement"
                tooltip="Edit Advertisement"
              />
            )}
            {canDeleteAds && (
              <DeleteButton
                onDelete={() => handleDeleteAdvertiesment(row)}
                itemName="advertisement"
                tooltip="Delete Advertisement"
              />
            )}
          </div>
        );
      }
    }
  ];

  // Only show actions column if user has any action permissions
  const shouldShowActionsColumn = canEditAds || canDeleteAds;
  const tableHeaders = shouldShowActionsColumn
    ? baseTableHeaders
    : baseTableHeaders.filter(header => header.key !== 'actions');

  // Handle row click if needed
  const handleRowClick = (row) => {
    if (!canReadAds) {
      toast.error('You do not have permission to view advertisement details');
      return;
    }
    console.log("Advertisement details:", row);
    // Show advertisement details or open a modal
  };

  // Show access denied message if user doesn't have read permission
  if (!canReadAds) {
    return (
      <div className="p-5">
        <PageHeader
          title={'Advertisement'}
          description={'Create and manage promotional advertisements'}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
            <p className="text-gray-600">You don't have permission to view advertisements.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <PageHeader
        title={'Advertisement'}
        description={'Create and manage promotional advertisements'}
      />
      <AppTable
        TH={tableHeaders}
        TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
        onRowClick={handleRowClick}
        isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
        emptyMessage="No advertisements found"
        buttonText={canAddAds ? "New Advertisement" : undefined}
        navigateTo={canAddAds ? "/addadvertisement" : undefined}
        TableSearch={
          <TableSearch
            options={options}
            onClick={handleSearch}
            optionSelect={optionSelect}
            setOptionSelect={setOptionSelect}
            setValueSearched={setValueSearched}
            valueSearched={valueSearched}
            onClear={handleClearSearch}
          />
        }
      />
    </div>
  );
}

export default Advertisement;